import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Mail, Phone, Plus, X } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { SalonContactInfo } from "@/lib/store/salon";
import { SalonContactType } from "@/lib/store/salon";
import { isValidSalonPhone } from "@/lib/utils/phone-validation";
import type { BusinessSettingsFormData } from "../type/business-settings";

interface ContactFieldConfig<T extends string = string> {
  formFieldPath: T;
  inputFieldType: string;
  inputPlaceholder: string;
  /** Whether a value is acceptable; `country` is the salon's. */
  isValidValue: (value: string, country: string) => boolean;
  invalidMessage: string;
  normalizeValue: (value: string) => string;
  duplicateMessage: string;
}

function getContactFieldConfigs(t: (key: string) => string) {
  return {
    [SalonContactType.EMAIL]: {
      formFieldPath: "emails",
      inputFieldType: "email",
      inputPlaceholder: t("business.contact.emailPlaceholder"),
      isValidValue: (value: string) => /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(value),
      invalidMessage: t("business.contact.invalidEmail"),
      normalizeValue: (value: string) => value.trim().toLowerCase(),
      duplicateMessage: t("business.contact.duplicateEmail"),
    },
    [SalonContactType.PHONE]: {
      formFieldPath: "phones",
      inputFieldType: "tel",
      inputPlaceholder: t("business.contact.phonePlaceholder"),
      // Same check as the backend, which reads national numbers in the salon's country
      isValidValue: (value: string, country: string) => isValidSalonPhone(value, country),
      invalidMessage: t("business.contact.invalidPhone"),
      normalizeValue: (value: string) => value.trim().replace(/[\s\-.()]/g, ""),
      duplicateMessage: t("business.contact.duplicatePhone"),
    },
  } as const;
}

function getContactFieldConfig<T extends SalonContactType>(
  contactType: T,
  configs: ReturnType<typeof getContactFieldConfigs>
): ContactFieldConfig<(typeof configs)[T]["formFieldPath"]> {
  const config = configs[contactType];
  if (!config) {
    throw new Error(`Unsupported contact type: ${contactType}`);
  }
  return config;
}

interface DraggableContactFieldProps {
  contactInfo: SalonContactInfo;
  fieldIndex: number;
  contactType: SalonContactType;
  isDisabled?: boolean;
  onRemoveContact: (index: number) => void;
}

function DraggableContactField({
  contactInfo,
  fieldIndex,
  contactType,
  isDisabled = false,
  onRemoveContact,
}: DraggableContactFieldProps) {
  const { t } = useTranslation("settings");
  const form = useFormContext<BusinessSettingsFormData>();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: contactInfo.id,
  });

  const configs = getContactFieldConfigs(t);
  const fieldConfig = getContactFieldConfig(contactType, configs);
  const {
    formFieldPath,
    inputFieldType,
    inputPlaceholder,
    isValidValue,
    invalidMessage,
    normalizeValue,
    duplicateMessage,
  } = fieldConfig;
  const formArrayPath = `contactInfo.${formFieldPath}` as const;

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const registrationFieldName = `contactInfo.${formFieldPath}.${fieldIndex}.value` as const;
  const fieldValidationError =
    formFieldPath === "emails"
      ? form.formState.errors.contactInfo?.emails?.[fieldIndex]?.value
      : form.formState.errors.contactInfo?.phones?.[fieldIndex]?.value;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 ${isDragging ? "opacity-50" : ""}`}
    >
      <div
        {...attributes}
        {...listeners}
        className="drag-handle cursor-grab active:cursor-grabbing p-1 -m-1 rounded hover:bg-muted flex-shrink-0"
      >
        <GripVertical className="w-4 h-4 text-muted-foreground" />
      </div>

      <div className="flex-1">
        <Input
          type={inputFieldType}
          placeholder={inputPlaceholder}
          {...form.register(registrationFieldName, {
            validate: {
              valid: (value) => {
                // Like the old `pattern` rule, an empty row passes; anything else,
                // spaces included, is checked as it will be sent.
                if (!value) return true;
                // Read the stored data live: `formState.defaultValues` is a snapshot
                // that can lag a `values` sync until the page re-renders.
                const stored = form.control._defaultValues;
                // The backend saves an untouched row as it is, so a number stored
                // before this check must not block saving the rest of the page.
                // `contactInfo.id` is the field array's key, so read the row's own id.
                const rowId = form.getValues(`${formArrayPath}.${fieldIndex}.id`);
                const storedRow = stored.contactInfo?.[formFieldPath]?.find(
                  (contact) => contact?.id === rowId
                );
                if (storedRow?.value === value) return true;
                // The backend checks against the salon's saved country, which the
                // country field on this page only changes once saved.
                const country = stored.basicInfo?.country ?? "";
                return isValidValue(value, country) || invalidMessage;
              },
              unique: (value) => {
                const normalizedValue = normalizeValue(value ?? "");
                if (!normalizedValue) return true;
                const isDuplicate = form
                  .getValues(formArrayPath)
                  .some(
                    (contact, index) =>
                      index !== fieldIndex &&
                      normalizeValue(contact.value ?? "") === normalizedValue
                  );
                return !isDuplicate || duplicateMessage;
              },
            },
            onChange: () => form.trigger(formArrayPath),
          })}
          disabled={isDisabled}
          className="contact-input h-9"
        />
        {fieldValidationError && (
          <p className="error-message text-xs text-destructive mt-1">
            {fieldValidationError.message}
          </p>
        )}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => onRemoveContact(fieldIndex)}
        disabled={isDisabled}
        className="remove-contact-btn h-8 w-8 p-0 flex-shrink-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
}

interface ContactSectionProps {
  contactType: SalonContactType;
  sectionTitle: string;
  sectionIcon: React.ComponentType<{ className?: string }>;
  isDisabled?: boolean;
}

export function ContactSection({
  contactType,
  sectionTitle,
  sectionIcon: SectionIcon,
  isDisabled = false,
}: ContactSectionProps) {
  const { t } = useTranslation("settings");
  const form = useFormContext<BusinessSettingsFormData>();
  const configs = getContactFieldConfigs(t);
  const fieldConfig = getContactFieldConfig(contactType, configs);
  const formArrayPath = `contactInfo.${fieldConfig.formFieldPath}` as const;

  const {
    fields: contactFields,
    append: addContact,
    remove: removeContact,
    move: reorderContact,
  } = useFieldArray({
    control: form.control,
    name: formArrayPath,
  });

  const dragSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    contactFields.forEach((_, index) => {
      form.setValue(`${formArrayPath}.${index}.order`, index + 1);
    });
  }, [contactFields, form, formArrayPath]);

  const handleAddNewContact = () => {
    const newContactInfo: SalonContactInfo = {
      id: `temp-${DateTime.now().toMillis()}`,
      salonId: "",
      franchiseId: "",
      type: contactType,
      value: "",
      label: null,
      order: contactFields.length + 1,
      createdAt: DateTime.now().toISO() ?? "",
      updatedAt: DateTime.now().toISO() ?? "",
    };
    addContact(newContactInfo);
  };

  const handleRemoveContact = (index: number) => {
    removeContact(index);
    void form.trigger(formArrayPath);
  };

  const handleContactReorder = (dragEvent: DragEndEvent) => {
    const { active, over } = dragEvent;
    if (active.id !== over?.id) {
      const currentIndex = contactFields.findIndex((contactField) => contactField.id === active.id);
      const targetIndex = contactFields.findIndex((contactField) => contactField.id === over?.id);
      if (currentIndex !== -1 && targetIndex !== -1) {
        reorderContact(currentIndex, targetIndex);
      }
    }
  };

  return (
    <div className="contact-section space-y-3">
      <div className="section-header flex items-center justify-between">
        <Label className="section-label text-sm font-medium flex items-center gap-2">
          <SectionIcon className="section-icon h-4 w-4" />
          {sectionTitle}
        </Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleAddNewContact}
          disabled={isDisabled}
          className="add-contact-btn h-8 px-2 text-xs"
        >
          <Plus className="h-3 w-3 mr-1" />
          {t("business.contact.add")}
        </Button>
      </div>

      <div className="contact-fields-container space-y-2">
        {contactFields.length === 0 ? (
          <Badge variant="secondary" className="empty-state-badge text-xs">
            {t("business.contact.noItems", { type: sectionTitle.toLowerCase() })}
          </Badge>
        ) : (
          <DndContext
            sensors={dragSensors}
            collisionDetection={closestCenter}
            onDragEnd={handleContactReorder}
          >
            <SortableContext
              items={contactFields.map((contactField) => contactField.id)}
              strategy={verticalListSortingStrategy}
            >
              {contactFields.map((contactField, fieldIndex) => (
                <DraggableContactField
                  key={contactField.id}
                  contactInfo={contactField}
                  fieldIndex={fieldIndex}
                  contactType={contactType}
                  isDisabled={isDisabled}
                  onRemoveContact={handleRemoveContact}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
}

interface ContactInformationFormProps {
  isDisabled?: boolean;
}

export function ContactInformationForm({ isDisabled = false }: ContactInformationFormProps) {
  const { t } = useTranslation("settings");

  return (
    <div className="space-y-6">
      <ContactSection
        contactType={SalonContactType.EMAIL}
        sectionTitle={t("business.contact.emailAddresses")}
        sectionIcon={Mail}
        isDisabled={isDisabled}
      />

      <ContactSection
        contactType={SalonContactType.PHONE}
        sectionTitle={t("business.contact.phoneNumbers")}
        sectionIcon={Phone}
        isDisabled={isDisabled}
      />
    </div>
  );
}
